import { useCallback, useEffect, useRef } from 'react';
import type { ObjectContextMentionData } from '@terra/shared-dto-chat-types';
import type { ChatRef } from '@terra/ui-design-system';
import { shouldRestoreComposerDraft } from './composer-draft-restore';
import { useChatTabsStore } from '../../store/chatTabsStore';
import { useMentions } from '../../hooks/useMentions';
import {
  parseTabMentionPayload,
  TERRA_TAB_MENTION_MIME,
} from '../../utils/mainContentTabMentionUtils';
import { resolveTabMentionWithContent } from '../../utils/resolveTabMentionWithContent';

export function useCopilotDraftAndDropHandlers({
  tabId,
  targetId,
  conversationId,
  isActive,
  status,
  draftInput,
  chatComponentRef,
  uploadFiles,
}: {
  tabId: string;
  targetId: string | undefined;
  conversationId: string | null;
  isActive: boolean;
  status: 'streaming' | 'submitted' | 'ready' | 'error';
  draftInput: string | undefined;
  chatComponentRef: React.RefObject<ChatRef | null>;
  uploadFiles: (files: File[]) => Promise<void> | void;
}) {
  const { updateChatTab } = useChatTabsStore();
  const { selectedMentions, insertMentionInEditor, addMention } = useMentions();

  const appliedDraftInputRef = useRef<string | null>(null);
  const sentDraftTextRef = useRef<string | null>(null);
  const previousIsActiveRef = useRef(isActive);
  const latestSelectedMentionsRef = useRef(selectedMentions);

  const handleChatDragOver = useCallback((event: React.DragEvent) => {
    if (!event.dataTransfer.types.includes(TERRA_TAB_MENTION_MIME)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleChatDrop = useCallback(
    async (event: React.DragEvent) => {
      const payloadRaw = event.dataTransfer.getData(TERRA_TAB_MENTION_MIME);

      if (!payloadRaw) {
        return;
      }

      event.preventDefault();
      const payload = parseTabMentionPayload(payloadRaw);

      if (!payload) {
        return;
      }

      const mention = await resolveTabMentionWithContent({
        payload,
        targetId,
      });

      if (!mention) {
        return;
      }

      addMention(mention);

      const objectContext = mention.mentionEntity?.extraData?.objectContext as
        | ObjectContextMentionData
        | undefined;

      if (objectContext?.objectKind === 'Finding') {
        updateChatTab(tabId, {
          linkedObject: {
            kind: 'Finding',
            id: objectContext.objectId,
            label: objectContext.objectLabel,
          },
        });
      }

      chatComponentRef.current?.focus();
    },
    [addMention, chatComponentRef, targetId, tabId, updateChatTab],
  );

  const handleChatPaste = useCallback(
    (event: React.ClipboardEvent) => {
      const clipboardData = event.clipboardData;

      if (!clipboardData) {
        return;
      }

      const imageFiles = Array.from(clipboardData.items)
        .filter(
          (item) => item.kind === 'file' && item.type.startsWith('image/'),
        )
        .flatMap((item) => {
          const file = item.getAsFile();

          return file ? [file] : [];
        });

      if (imageFiles.length === 0) {
        return;
      }

      const hasPlainText = clipboardData.getData('text/plain').length > 0;

      if (!hasPlainText) {
        event.preventDefault();
      }

      const timestamp = Date.now();
      const pastedFiles = imageFiles.map((file, index) => {
        const extension = file.type.split('/')[1] ?? 'png';
        const suffix = index > 0 ? `-${index + 1}` : '';

        return new File(
          [file],
          `screenshot-${timestamp}${suffix}.${extension}`,
          { type: file.type },
        );
      });

      void uploadFiles(pastedFiles);
    },
    [uploadFiles],
  );

  useEffect(() => {
    if (
      !draftInput ||
      !shouldRestoreComposerDraft({
        isActive,
        draftInput,
        status,
        appliedDraftInput: appliedDraftInputRef.current,
        sentDraftText: sentDraftTextRef.current,
      })
    ) {
      return;
    }

    const draftToRestore = draftInput;
    const chatComponent = chatComponentRef.current;

    if (!chatComponent) {
      return;
    }

    const existingComposerText = chatComponent.getInput().trim();

    if (existingComposerText.length > 0) {
      appliedDraftInputRef.current = draftToRestore;

      return;
    }

    const capturedDraftInput = draftToRestore;
    const tabMentions =
      useChatTabsStore
        .getState()
        .chatTabs.find((chatTab) => chatTab.tabId === tabId)?.mentions ?? [];
    const timerId = setTimeout(() => {
      chatComponent.appendText(capturedDraftInput);
      for (const mention of tabMentions) {
        if (mention.mentionEntity) {
          insertMentionInEditor(mention.mentionEntity);
        }
      }

      chatComponent.focus();
      updateChatTab(tabId, { draftInput: undefined });
      appliedDraftInputRef.current = capturedDraftInput;
    }, 0);

    return () => clearTimeout(timerId);
  }, [
    chatComponentRef,
    draftInput,
    insertMentionInEditor,
    isActive,
    status,
    tabId,
    updateChatTab,
  ]);

  useEffect(() => {
    if (previousIsActiveRef.current && !isActive) {
      const composerText = chatComponentRef.current?.getInput().trim() ?? '';

      if (composerText.length > 0) {
        updateChatTab(tabId, { draftInput: composerText });
      } else if (!draftInput || appliedDraftInputRef.current === draftInput) {
        updateChatTab(tabId, { draftInput: undefined });
      }
    }

    previousIsActiveRef.current = isActive;
  }, [chatComponentRef, draftInput, isActive, tabId, updateChatTab]);

  const handleComposerInputChange = useCallback(
    (value: string) => {
      if (!isActive || conversationId) {
        return;
      }

      const composerText = value.trim();

      if (
        sentDraftTextRef.current &&
        composerText !== sentDraftTextRef.current
      ) {
        sentDraftTextRef.current = null;
      }

      const nextDraftInput = composerText.length > 0 ? composerText : undefined;
      const currentTab = useChatTabsStore
        .getState()
        .chatTabs.find((chatTab) => chatTab.tabId === tabId);

      if (currentTab?.draftInput !== nextDraftInput) {
        updateChatTab(tabId, { draftInput: nextDraftInput });
      }
    },
    [conversationId, isActive, tabId, updateChatTab],
  );

  useEffect(() => {
    latestSelectedMentionsRef.current = selectedMentions;
  }, [selectedMentions]);

  useEffect(() => {
    return () => {
      useChatTabsStore.getState().updateChatTab(tabId, {
        mentions: latestSelectedMentionsRef.current.map((mention) => ({
          id: mention.id,
          label: mention.label,
          type: mention.type,
          mentionEntity: mention.mentionEntity,
        })),
      });
    };
  }, [tabId]);

  return {
    appliedDraftInputRef,
    sentDraftTextRef,
    handleChatDragOver,
    handleChatDrop,
    handleChatPaste,
    handleComposerInputChange,
  };
}
